import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { getErrorMessage } from "../../api/http";
import { FormField } from "../common/FormField";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

/// Modal de auto-serviço: o usuário logado altera o próprio nome e/ou senha.
export function ProfileDialog({ visible, onHide }: { visible: boolean; onHide: () => void }) {
    const { user, updateProfile } = useAuth();
    const toast = useToast();
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    // Ao abrir, pré-carrega o nome atual e limpa os campos de senha.
    useEffect(() => {
        if (visible) {
            setName(user?.name ?? "");
            setPassword("");
            setConfirm("");
        }
    }, [visible, user?.name]);

    const trimmedName = name.trim();
    const nameChanged = trimmedName.length > 0 && trimmedName !== user?.name;
    const wantsPassword = password.length > 0 || confirm.length > 0;
    const passwordValid = !wantsPassword || (password.length >= 8 && password === confirm);
    const canSave = (nameChanged || wantsPassword) && passwordValid && trimmedName.length >= 2;

    const save = async () => {
        if (!canSave) return;
        try {
            setBusy(true);
            await updateProfile({
                name: nameChanged ? trimmedName : undefined,
                password: wantsPassword ? password : undefined,
            });
            toast.success("Dados atualizados", wantsPassword ? "Nome e senha salvos." : "Nome salvo.");
            onHide();
        } catch (error) {
            toast.error("Não foi possível atualizar", getErrorMessage(error));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            header="Meus dados"
            visible={visible}
            onHide={() => !busy && onHide()}
            className="responsive-dialog"
            blockScroll
            footer={
                <>
                    <Button label="Cancelar" text severity="secondary" onClick={onHide} disabled={busy} />
                    <Button label="Salvar" icon="pi pi-check" loading={busy} disabled={!canSave} onClick={() => void save()} />
                </>
            }
        >
            <div className="form-grid">
                <FormField label="Usuário">
                    <InputText value={user?.username ?? ""} disabled />
                </FormField>
                <FormField label="Nome" required>
                    <InputText value={name} onChange={(event) => setName(event.target.value)} />
                </FormField>
                <FormField label="Nova senha" hint="Deixe em branco para manter a atual (mínimo 8 caracteres)">
                    <Password
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        toggleMask
                        feedback={false}
                        inputStyle={{ width: "100%" }}
                        style={{ width: "100%" }}
                    />
                </FormField>
                <FormField
                    label="Confirmar nova senha"
                    hint={wantsPassword && confirm.length > 0 && password !== confirm ? "As senhas não conferem" : undefined}
                >
                    <Password
                        value={confirm}
                        onChange={(event) => setConfirm(event.target.value)}
                        toggleMask
                        feedback={false}
                        inputStyle={{ width: "100%" }}
                        style={{ width: "100%" }}
                    />
                </FormField>
            </div>
        </Dialog>
    );
}
